import React, { useEffect, useMemo, useState, useCallback, useLayoutEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import { getApiErrorMessage, handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import MonthYearPicker from "../../Components/MonthYearPicker/MonthYearPicker";
import { getDefaultSalaryAwardsMonth } from "../../Components/MonthYearPicker/monthYearBounds";
import Toastify, { notifyError } from "../../utils/tostfy";
import {
  SEARCH_MAX_LENGTH,
  clampSearchInput,
  normalizePastedSearchInput,
} from "../../utils/search";
import { useListPersistence } from "../../hooks/useListPersistence";
import { formatBalanceRupees, formatInr, formatSummaryRupees } from "../../utils/formatAmount";
import { readListLimit, readListPage } from "../../hooks/useListQueryPersistence";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const LIST_PATH = "/salary-awards";

/** Theme (from design spec) */
const C = {
  bg: "#FFFFFF",
  card: "#F5F5F5",
  thead: "#000053",
  text: "#000000",
  muted: "#757575",
  border: "#E0E0E0",
};

const BALANCE_FILTER_OPTIONS = [
  { value: "all", label: "All Drivers" },
  { value: "positive_balance", label: "Positive Balance" },
  { value: "negative_balance", label: "Negative Balance" },
  { value: "zero_balance", label: "Zero Balance" },
];

const normalizeBalanceFilter = (raw) => {
  const legacyMap = {
    positive: "positive_balance",
    negative: "negative_balance",
    zero: "zero_balance",
  };
  const value = String(raw || "all").trim();
  if (value === "all") return "all";
  return legacyMap[value] || value;
};

const readBalanceFilterFromParams = (searchParams) =>
  normalizeBalanceFilter(
    searchParams.get("balance_status") || searchParams.get("balance")
  );

function formatMobileDisplay(digits) {
  const d = String(digits || "").replace(/\D/g, "");
  if (d.length === 10) {
    return `${d.slice(0, 3)} ${d.slice(3, 7)} ${d.slice(7)}`;
  }
  return digits || "—";
}

function formatLastPayment(isoOrDate) {
  if (!isoOrDate) return "—";
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

const mapLocation = (item) => ({
  id: item?._id || item?.id || "",
  name: item?.name || item?.location_name || "Unknown Location",
});

const mapDriverSalaryRow = (r) => ({
  id: r?._id ?? r?.id ?? "",
  driverRefId: r?.driver_ref_id != null ? String(r.driver_ref_id) : "—",
  driverName: r?.full_name ?? "—",
  license: r?.license_number ?? "—",
  mobile: r?.mobile_number ?? "",
  miplLocation:
    (typeof r?.mipl_location_id === "object" && r?.mipl_location_id?.name) ||
    r?.mipl_location?.name ||
    r?.mipl_location_name ||
    "—",
  balanceRupees: Number(r?.current_balance ?? r?.salary_amount ?? 0),
  lastPayment: r?.last_payment ?? null,
  tripsThisMonth: r?.total_trip ?? 0,
});

function parseMonthYear(value) {
  const [y, m] = String(value || "").split("-");
  return {
    year: y || String(new Date().getFullYear()),
    month: m ? String(m).padStart(2, "0") : String(new Date().getMonth() + 1).padStart(2, "0"),
  };
}

const SalaryDisbursement = () => {
  const [searchParams] = useSearchParams();
  const [drivers, setDrivers] = useState([]);
  const [summary, setSummary] = useState({
    totalDrivers: 0,
    totalOutstanding: 0,
    totalNegative: 0,
    paymentThisMonth: 0,
    totalPayoutAmount: 0,
    totalAwardAmount: 0,
  });
  const [listLoading, setListLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [balanceFilter, setBalanceFilter] = useState(() =>
    readBalanceFilterFromParams(searchParams)
  );
  const [miplLocationId, setMiplLocationId] = useState(
    () => searchParams.get("mipl_location_id") || ""
  );
  const [miplLocations, setMiplLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    clampSearchInput(searchParams.get("search") || "").trim()
  );
  const defaultMonth = getDefaultSalaryAwardsMonth();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const m = searchParams.get("month");
    if (m) return parseMonthYear(m).month || defaultMonth.month;
    return defaultMonth.month;
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    const m = searchParams.get("month");
    if (m) return parseMonthYear(m).year || defaultMonth.year;
    return defaultMonth.year;
  });
  const [currentPage, setCurrentPage] = useState(() => readListPage(searchParams));
  const [itemsPerPage, setItemsPerPage] = useState(() => readListLimit(searchParams));

  const applyRestoredState = useCallback((saved) => {
    if (saved.search != null) {
      setSearch(saved.search);
      setDebouncedSearch(clampSearchInput(saved.search).trim());
    }
    if (saved.page) {
      const p = Number(saved.page);
      if (Number.isFinite(p) && p > 0) setCurrentPage(p);
    }
    if (saved.limit) {
      const l = Number(saved.limit);
      if (Number.isFinite(l) && l > 0) setItemsPerPage(l);
    }
    if (saved.balance_status) {
      setBalanceFilter(normalizeBalanceFilter(saved.balance_status));
    } else if (saved.balance) {
      setBalanceFilter(normalizeBalanceFilter(saved.balance));
    }
    if (saved.mipl_location_id) setMiplLocationId(saved.mipl_location_id);
    if (saved.month) {
      const { year, month } = parseMonthYear(saved.month);
      if (year) setSelectedYear(year);
      if (month) setSelectedMonth(month);
    }
  }, []);

  const monthValue = useMemo(
    () => `${selectedYear}-${selectedMonth}`,
    [selectedYear, selectedMonth]
  );

  const { skipPageResetRef, detailListQuery, returnToParam } = useListPersistence(
    LIST_PATH,
    {
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearch || undefined,
      balance_status: balanceFilter !== "all" ? balanceFilter : undefined,
      mipl_location_id: miplLocationId || undefined,
      month: monthValue || undefined,
    },
    applyRestoredState
  );

  const selectedMonthLabel = useMemo(() => {
    const y = Number(selectedYear);
    const m = Number(selectedMonth);
    if (!y || !m) return "";
    return new Date(y, m - 1, 1).toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });
  }, [selectedMonth, selectedYear]);

  /** Summary cards show one month before the filter selection (e.g. June → May). */
  const summaryMonthLabel = useMemo(() => {
    const y = Number(selectedYear);
    const m = Number(selectedMonth);
    if (!y || !m) return "";
    return new Date(y, m - 2, 1).toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric",
    });
  }, [selectedMonth, selectedYear]);

  const paymentHistoryLink = useMemo(() => {
    const params = new URLSearchParams();
    if (miplLocationId) params.set("mipl_location_id", miplLocationId);
    if (returnToParam) params.set("returnTo", returnToParam);
    const qs = params.toString();
    return `/salary-awards/salary-and-awards-detail/view-transactions${
      qs ? `?${qs}` : ""
    }`;
  }, [miplLocationId, returnToParam]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const fromUrl = searchParams.get("mipl_location_id") || "";
    setMiplLocationId((prev) => (prev === fromUrl ? prev : fromUrl));
  }, [searchParams]);

  const handleSearchPaste = (event) => {
    event.preventDefault();
    setSearch(normalizePastedSearchInput(event.clipboardData.getData("text")));
  };

  useLayoutEffect(() => {
    if (skipPageResetRef.current) return;
    setCurrentPage(1);
  }, [debouncedSearch, monthValue, balanceFilter, miplLocationId, skipPageResetRef]);

  useEffect(() => {
    const fetchLocations = async () => {
      setLocationsLoading(true);
      try {
        const response = await handleApiCall("get", endpoint.listOfAlllocation, {});
        if (response?.data?.code === 200) {
          const d = response?.data?.data;
          const raw = Array.isArray(d)
            ? d
            : Array.isArray(d?.location_data)
              ? d.location_data
              : [];
          setMiplLocations(raw.map(mapLocation).filter((x) => x.id && x.name));
        } else {
          setMiplLocations([]);
        }
      } catch {
        setMiplLocations([]);
      } finally {
        setLocationsLoading(false);
      }
    };
    fetchLocations();
  }, []);

  const summaryCards = useMemo(() => {
    const totalSalary = Number(summary.totalPayoutAmount ?? 0);
    const awardBalance = Number(summary.totalAwardAmount ?? 0);
    const totalNegative = Math.abs(Number(summary.totalNegative) || 0);
    const negativeBalanceAmount = totalNegative > 0 ? -totalNegative : 0;

    return [
      {
        label: "Total Drivers",
        value: summary.totalDrivers.toLocaleString("en-IN"),
        sub: "Active",
      },
      {
        label: "Total Outstanding Balance",
        value: formatSummaryRupees(summary.totalOutstanding),
        sub: "Across all drivers",
      },
      {
        label: "Negative Balance",
        value:
          totalNegative > 0
            ? formatInr(negativeBalanceAmount)
            : formatSummaryRupees(0),
        rawAmount: totalNegative > 0 ? negativeBalanceAmount : undefined,
        sub: "Drivers need attention",
        valueRed: true,
      },
      {
        label: "Payment This Month",
        value: formatSummaryRupees(summary.paymentThisMonth),
        sub: summaryMonthLabel || "Selected month",
      },
      {
        label: "Total Salary",
        value: formatBalanceRupees(totalSalary),
        rawAmount: totalSalary,
        sub: summaryMonthLabel || "Selected month",
      },
      {
        label: "Award Balance",
        value: formatBalanceRupees(awardBalance),
        rawAmount: awardBalance,
        sub: "",
      },
    ];
  }, [summary, summaryMonthLabel]);

  const fetchDriverSalaryList = async () => {
    setListLoading(true);
    try {
      const { month, year } = parseMonthYear(monthValue);
      const q = [
        `page=${currentPage}`,
        `limit=${itemsPerPage}`,
        `search=${encodeURIComponent(clampSearchInput(debouncedSearch).trim())}`,
        `month=${month}`,
        `year=${year}`,
        ...(miplLocationId ? [`mipl_location_id=${encodeURIComponent(miplLocationId)}`] : []),
        ...(balanceFilter !== "all"
          ? [`balance_status=${encodeURIComponent(balanceFilter)}`]
          : []),
      ].join("&");

      const response = await handleApiCall(
        "get",
        `${endpoint.listOfDriverSalary}?${q}`,
        {}
      );

      if (response?.data?.code === 200) {
        const d = response?.data?.data || {};
        const raw = d?.driver_data;
        const list = Array.isArray(raw) ? raw : [];
        setDrivers(list.map(mapDriverSalaryRow));
        setSummary({
          totalDrivers: d?.total_no_of_driver ?? d?.total ?? 0,
          totalOutstanding: d?.total_outstanding_balance ?? 0,
          totalNegative: d?.total_negative ?? 0,
          paymentThisMonth: d?.payment_of_this_month ?? 0,
          totalPayoutAmount: d?.total_payout_amount ?? 0,
          totalAwardAmount: d?.total_award_amount ?? 0,
        });
        setTotalRecords(d?.total ?? list.length);
        setTotalPages(Math.max(1, d?.total_pages ?? 1));
      } else {
        setDrivers([]);
        setSummary({
          totalDrivers: 0,
          totalOutstanding: 0,
          totalNegative: 0,
          paymentThisMonth: 0,
          totalPayoutAmount: 0,
          totalAwardAmount: 0,
        });
        setTotalRecords(0);
        setTotalPages(1);
        // const errMsg =
        //   response?.response?.data?.errorMessage ||
        //   response?.response?.data?.message ||
        //   response?.data?.message ||
        //   "Failed to load driver salary list";
        // notifyError(errMsg);
        notifyError(getApiErrorMessage(response, 'Failed to load driver salary list'));
      }
    } catch {
      setDrivers([]);
      setSummary({
        totalDrivers: 0,
        totalOutstanding: 0,
        totalNegative: 0,
        paymentThisMonth: 0,
        totalPayoutAmount: 0,
        totalAwardAmount: 0,
      });
      setTotalRecords(0);
      setTotalPages(1);
      notifyError("Failed to load driver salary list");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchDriverSalaryList();
  }, [debouncedSearch, currentPage, itemsPerPage, monthValue, miplLocationId, balanceFilter]);

  const recordsLabel = `${totalRecords} records found`;

  return (
    <>
      <Toastify />
      <div
        className="tab-pane-content md:mt-7 !mt-4 font-UbuntuRegular"
        style={{ borderRadius: "10px" }}
      >
        {/* Summary cards */}
        <div className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 md:gap-4 gap-2 mb-8">
          {summaryCards.map((card) => (
            <div
              key={card.label}
              className="rounded-xl md:px-5 md:py-4 px-3 py-2 border bg-white shadow-sm border-[#E0E0E0]"
              style={{ backgroundColor: C.card }}
            >
              <p className="md:text-sm text-xs mb-1" style={{ color: C.muted }}>
                {card.label}
              </p>
              <p
                className={`md:text-2xl  font-UbuntuBold mb-1 ${
                  card.valueRed || (card.rawAmount != null && Number(card.rawAmount) < 0)
                    ? "text-red-600"
                    : card.value.replace(/\D/g, "").length > 10
                    ? "text-xs"
                    : ""
                }`}
                style={
                  card.valueRed || (card.rawAmount != null && Number(card.rawAmount) < 0)
                    ? undefined
                    : { color: C.text }
                }
              >
                {card.value}
              </p>
              {card.sub ? (
                <p className="text-xs" style={{ color: C.muted }}>
                  {card.sub}
                </p>
              ) : null}
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-4 lg:gap-6 md:mb-6 mb-2">
          <div className="min-w-[160px] flex-1 sm:flex-none">
            <label
              className="block text-xs font-UbuntuMedium mb-1.5"
              style={{ color: C.muted }}
              htmlFor="balance-status"
            >
              Balance Status
            </label>
            <select
              id="balance-status"
              className="form-control h-11 rounded-lg border text-sm w-full max-w-xs"
              style={{ borderColor: C.border, color: C.text }}
              value={balanceFilter}
              onChange={(e) => setBalanceFilter(e.target.value)}
            >
              {BALANCE_FILTER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="min-w-[180px] flex-1 sm:flex-none">
            <label
              className="block text-xs font-UbuntuMedium mb-1.5"
              style={{ color: C.muted }}
              htmlFor="mipl-location"
            >
              MIPL Location
            </label>
            <select
              id="mipl-location"
              className="form-control h-11 rounded-lg border text-sm w-full max-w-xs"
              style={{ borderColor: C.border, color: C.text }}
              value={miplLocationId}
              onChange={(e) => setMiplLocationId(e.target.value)}
              disabled={locationsLoading}
            >
              <option value="">
                {locationsLoading ? "Loading locations…" : "All MIPL locations"}
              </option>
              {miplLocations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[200px] max-w-md">
            <label
              className="block text-xs font-UbuntuMedium mb-1.5"
              style={{ color: C.muted }}
              htmlFor="driver-search"
            >
              Search Driver
            </label>
            <input
              id="driver-search"

              type="search"
              placeholder="Search by name or ID..."
              className="border px-3 py-2.5 rounded-lg outline-none text-sm searchIcon bg-white focus:!shadow-sm w-full h-11"
              style={{ borderColor: C.border }}
              value={search}
                        maxLength={SEARCH_MAX_LENGTH}

                        onChange={(e) => setSearch(clampSearchInput(e.target.value))}
                        onPaste={handleSearchPaste}
            />
          </div>

          <div className="max-[767px]:w-full">
            <MonthYearPicker
              id="salary-month-year"
              month={selectedMonth}
              year={selectedYear}
              onChange={({ month, year }) => {
                setSelectedMonth(month);
                setSelectedYear(year);
              }}
            />
          </div>

          <div className="w-full sm:w-auto sm:ml-auto flex justify-end [&_a]:!px-6">
            <Link
              to={paymentHistoryLink}
              className="btn btn-primary ml-auto !text-sm"
            >
              Payment History
            </Link>
          </div>
        </div>

        {/* Table */}
        <div
          className="common-table-wrapper m-0 rounded-xl border shadow-sm"
          style={{ borderColor: C.border }}
        >
          <div className="m-0 w-full overflow-x-auto">
            <table className="mb-0 w-full min-w-[1200px] bg-white">
              <thead>
                <tr>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0">
                    S.No.
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0">
                    Driver ID
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0">
                    Driver Name
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0">
                    Driver License Number
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0">
                    Mobile Number
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0">
                    MIPL Location
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0">
                    Current Balance
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0">
                    Last Payment Date
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0">
                    Trips (This Month)
                  </th>
                  <th className="!text-white !font-UbuntuMedium text-xs md:text-sm whitespace-nowrap px-3 py-3.5 border-0 text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {listLoading ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="text-center py-10 text-sm"
                      style={{ color: C.muted }}
                    >
                      Loading...
                    </td>
                  </tr>
                ) : drivers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="text-center py-10 text-sm"
                      style={{ color: C.muted }}
                    >
                      No drivers match your filters.
                    </td>
                  </tr>
                ) : (
                  drivers.map((driver, index) => (
                    <tr
                      key={driver.id}
                      className={index % 2 === 0 ? "bg-white" : "bg-[#FAFAFA]"}
                      style={{ borderColor: C.border }}
                    >
                      <td className="align-middle px-3 md:py-3 !py-1.5 text-sm text-black">
                        {String((currentPage - 1) * itemsPerPage + index + 1).padStart(
                          2,
                          "0"
                        )}
                      </td>
                      <td className="align-middle px-3 md:py-3 !py-1.5 text-sm" style={{ color: C.muted }}>
                        {driver.driverRefId}
                      </td>
                      <td className="align-middle px-3 md:py-3 !py-1.5 text-sm font-medium text-black">
                        {driver.driverName}
                      </td>
                      <td className="align-middle px-3 md:py-3 !py-1.5 text-sm text-black">
                        {driver.license}
                      </td>
                      <td className="align-middle px-3 md:py-3 !py-1.5 text-sm text-black whitespace-nowrap">
                        {formatMobileDisplay(driver.mobile)}
                      </td>
                      <td className="align-middle px-3 md:py-3 !py-1.5 text-sm text-black">
                        {driver.miplLocation}
                      </td>
                      <td className="align-middle px-3 md:py-3 !py-1.5 text-sm whitespace-nowrap font-medium">
                        <span
                          className={
                            driver.balanceRupees < 0 ? "text-red-600" : "text-black"
                          }
                        >
                          {formatBalanceRupees(driver.balanceRupees)}
                        </span>
                      </td>
                      <td className="align-middle px-3 md:py-3 !py-1.5 text-sm text-black whitespace-nowrap">
                        {formatLastPayment(driver.lastPayment)}
                      </td>
                      <td className="align-middle px-3 md:py-3 !py-1.5 text-sm text-black">
                        {driver.tripsThisMonth} Trips
                      </td>
                      <td className="align-middle px-3 md:py-3 !py-1.5">
                        <div className="flex flex-wrap items-center justify-center gap-2">
                          <Link
                            to={`/salary-awards/salary-and-awards-detail?driver_id=${encodeURIComponent(driver.id)}${detailListQuery}`}
                            className="inline-flex items-center justify-center rounded-full px-3 md:px-4 py-2 text-xs font-UbuntuMedium  transition-colors hover:bg-gray-50 text-white"
                            style={{
                              color: C.text,
                              backgroundColor: "#f7a220",
                              border: `1px solid ${C.border}  `,
                            }}
                          >
                            View Ledger
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex gap-x-4 items-center mt-4 flex-wrap">
          <select
            className="p-2 shadow-sm outline-none max-[767px]:text-xs"
            style={{ borderColor: C.border }}
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
          <div className="text-xs md:text-sm text-gray-500 ml-auto" style={{ color: C.muted }}>
            {recordsLabel}
          </div>
          <div className="flex items-center gap-2 lg:pr-8">
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || listLoading}
              className="!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center "
            >
              <img src={LeftAngle} className="invert grayscale brightness-100" alt="" />
            </button>
            <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
            <span className="text-sm text-gray-600">of {totalPages}</span>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || listLoading}
              className="!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center "
            >
              <img src={RightAngle} className="invert grayscale brightness-100" alt="" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SalaryDisbursement;
